import React, { useCallback, useMemo, useState, useEffect } from 'react';
import API_BASE_URL from '../../api';
import GoldRepledgeModal from '../Loans/GoldRepledgeModal';

const formatAmount = value => (Number(value) || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatDate = value => value ? String(value).slice(0, 10) : '';

const isGoldLoan = loan => {
  const loanType = String(loan?.loan_type || '').toLowerCase();
  return loanType === 'gl' || loanType.includes('gold');
};

const isOpenLoan = loan => (
  String(loan?.status || '').toLowerCase() !== 'closed' && loan?.loan_status_closed !== true
);

const getPledgeStatus = loan => loan.repledge_status || 'Not Pledged';

const badgeColors = {
  'Not Pledged': { background: '#eef2f6', color: '#475467' },
  Pledged: { background: '#dcfce7', color: '#166534' },
  Overdue: { background: '#fee2e2', color: '#b42318' },
  Released: { background: '#dbeafe', color: '#1d4ed8' },
};

function StatusBadge({ status }) {
  const colors = badgeColors[status] || badgeColors['Not Pledged'];
  return <span style={{ ...colors, display: 'inline-block', padding: '3px 8px', borderRadius: 999, fontWeight: 700, whiteSpace: 'nowrap' }}>{status}</span>;
}

function GoldRepledges() {
  const [loans, setLoans] = useState([]);
  const [selectedLoanIds, setSelectedLoanIds] = useState([]);
  const [releaseLoan, setReleaseLoan] = useState(null);
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPledgeModal, setShowPledgeModal] = useState(false);

  const loadLoans = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(`${API_BASE_URL}/api/loans`)
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Unable to load Gold Loans');
        return data;
      })
      .then(data => {
        const goldLoans = (Array.isArray(data) ? data : []).filter(loan => isGoldLoan(loan) && isOpenLoan(loan));
        setLoans(goldLoans);
        setSelectedLoanIds(current => current.filter(loanId => {
          const loan = goldLoans.find(item => item.loan_id === loanId);
          return loan && ['Not Pledged', 'Released'].includes(getPledgeStatus(loan));
        }));
        setReleaseLoan(current => goldLoans.find(loan => loan.loan_id === current?.loan_id) || null);
      })
      .catch(err => {
        setLoans([]);
        setSelectedLoanIds([]);
        setReleaseLoan(null);
        setError(err.message || 'Unable to load Gold Loans');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadLoans();
  }, [loadLoans]);

  const filteredLoans = useMemo(() => {
    const search = filter.trim().toLowerCase();
    return loans.filter(loan => {
      const matchesStatus = statusFilter === 'all' || getPledgeStatus(loan) === statusFilter;
      const matchesSearch = !search || [loan.loan_id, loan.customer_id, loan.customer_name, loan.repledge_bank_name, loan.pledge_number]
        .some(value => String(value || '').toLowerCase().includes(search));
      return matchesStatus && matchesSearch;
    });
  }, [loans, filter, statusFilter]);

  const availableCount = loans.filter(loan => ['Not Pledged', 'Released'].includes(getPledgeStatus(loan))).length;
  const pledgedCount = loans.filter(loan => ['Pledged', 'Overdue'].includes(getPledgeStatus(loan))).length;
  const selectedLoans = loans.filter(loan => selectedLoanIds.includes(loan.loan_id));
  const selectableFilteredLoans = filteredLoans.filter(loan => ['Not Pledged', 'Released'].includes(getPledgeStatus(loan)));
  const allVisibleSelected = selectableFilteredLoans.length > 0 && selectableFilteredLoans.every(loan => selectedLoanIds.includes(loan.loan_id));

  const toggleLoan = loan => {
    if (!['Not Pledged', 'Released'].includes(getPledgeStatus(loan))) return;
    setSelectedLoanIds(current => current.includes(loan.loan_id)
      ? current.filter(loanId => loanId !== loan.loan_id)
      : [...current, loan.loan_id]);
  };

  const toggleAllVisible = () => {
    const visibleLoanIds = selectableFilteredLoans.map(loan => loan.loan_id);
    setSelectedLoanIds(current => allVisibleSelected
      ? current.filter(loanId => !visibleLoanIds.includes(loanId))
      : [...new Set([...current, ...visibleLoanIds])]);
  };

  const completeAction = () => {
    setShowPledgeModal(false);
    setReleaseLoan(null);
    setSelectedLoanIds([]);
    loadLoans();
  };

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: 'navy', margin: '0 0 6px' }}>Gold Re-Pledge</h2>
      <div style={{ color: '#667085', marginBottom: 18 }}>Select one or more available Gold Loans and create a single bank pledge for the group.</div>

      <div className="loan-totals-strip" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px' }}><span style={{ color: '#667085' }}>Open Gold Loans</span><br /><strong>{loans.length}</strong></div>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px' }}><span style={{ color: '#667085' }}>Available to Pledge</span><br /><strong>{availableCount}</strong></div>
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px' }}><span style={{ color: '#667085' }}>Currently Pledged</span><br /><strong>{pledgedCount}</strong></div>
      </div>

      <div className="mobile-toolbar" style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
        <input value={filter} onChange={event => setFilter(event.target.value)} placeholder="Search loan or customer..." style={{ padding: 7, minWidth: 240, border: '1px solid #cfd8e3', borderRadius: 4 }} />
        <select value={statusFilter} onChange={event => setStatusFilter(event.target.value)} style={{ padding: 7, border: '1px solid #cfd8e3', borderRadius: 4 }}>
          <option value="all">All Statuses</option>
          <option value="Not Pledged">Not Pledged</option>
          <option value="Pledged">Pledged</option>
          <option value="Overdue">Overdue</option>
          <option value="Released">Released</option>
        </select>
        <button type="button" onClick={loadLoans} style={{ padding: '7px 16px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 4 }}>Refresh</button>
        <button type="button" disabled={!selectedLoans.length} onClick={() => selectedLoans.length && setShowPledgeModal(true)} style={{ padding: '7px 16px', background: selectedLoans.length ? '#7c3aed' : '#eee', color: selectedLoans.length ? '#fff' : '#888', border: 'none', borderRadius: 4, fontWeight: 700, cursor: selectedLoans.length ? 'pointer' : 'not-allowed' }}>
          Re-Pledge Selected ({selectedLoans.length})
        </button>
      </div>

      {error && <div style={{ color: '#b42318', background: '#fff1f0', border: '1px solid #fecdca', padding: 10, borderRadius: 4, marginBottom: 12 }}>{error}</div>}

      <div className="desktop-table-wrap">
        <table className="fixed-header-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead><tr style={{ background: '#fafbfc' }}>
            <th style={{ textAlign: 'left', padding: '7px 8px', borderBottom: '1px solid #d0d5dd' }}>
              <input type="checkbox" aria-label="Select all available visible loans" checked={allVisibleSelected} onChange={toggleAllVisible} disabled={!selectableFilteredLoans.length} />
            </th>
            {['Loan ID', 'Customer', 'Issue Date', 'Loan Amount', 'Balance', 'Re-Pledge Status', 'Bank / Pledge No.', 'Action'].map(label => <th key={label} style={{ textAlign: 'left', padding: '7px 8px', borderBottom: '1px solid #d0d5dd' }}>{label}</th>)}
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="9" style={{ padding: 18 }}>Loading open Gold Loans...</td></tr>
            ) : filteredLoans.length === 0 ? (
              <tr><td colSpan="9" style={{ padding: 18 }}>No matching open Gold Loans.</td></tr>
            ) : filteredLoans.map(loan => {
              const status = getPledgeStatus(loan);
              const selectable = ['Not Pledged', 'Released'].includes(status);
              const selected = selectedLoanIds.includes(loan.loan_id);
              return (
                <tr key={loan.loan_id} className={selected ? 'selected-record-row' : undefined} onClick={() => toggleLoan(loan)} style={{ cursor: selectable ? 'pointer' : 'default' }}>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}><input type="checkbox" checked={selected} disabled={!selectable} onChange={() => toggleLoan(loan)} onClick={event => event.stopPropagation()} /></td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee', fontWeight: 700 }}>{loan.loan_id}</td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}>{loan.customer_id} - {loan.customer_name}</td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}>{formatDate(loan.issue_date)}</td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}>{formatAmount(loan.issue_amount)}</td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}>{formatAmount(loan.balance_amount)}</td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}><StatusBadge status={status} /></td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}>{loan.repledge_bank_name ? `${loan.repledge_bank_name} / ${loan.pledge_number}` : '-'}</td>
                  <td style={{ padding: 7, borderBottom: '1px solid #eee' }}>
                    {['Pledged', 'Overdue'].includes(status) && <button type="button" onClick={event => { event.stopPropagation(); setReleaseLoan(loan); }} style={{ padding: '5px 10px', border: '1px solid #d97706', borderRadius: 4, background: '#fff7ed', color: '#9a3412' }}>View / Release</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mobile-card-list">
        {filteredLoans.map(loan => {
          const status = getPledgeStatus(loan);
          const selectable = ['Not Pledged', 'Released'].includes(status);
          const selected = selectedLoanIds.includes(loan.loan_id);
          return (
            <div key={loan.loan_id} className={`mobile-record-card ${selected ? 'selected' : ''}`} onClick={() => toggleLoan(loan)} style={{ cursor: selectable ? 'pointer' : 'default' }}>
              <div className="mobile-card-title">
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <input type="checkbox" checked={selected} disabled={!selectable} onChange={() => toggleLoan(loan)} onClick={event => event.stopPropagation()} />
                  <div>{loan.customer_name}<div className="mobile-card-subtitle">{loan.loan_id}</div></div>
                </div>
                <StatusBadge status={status} />
              </div>
              <div className="mobile-card-grid">
                <div className="mobile-card-field"><span className="mobile-card-label">Issued</span><span className="mobile-card-value">{formatAmount(loan.issue_amount)}</span></div>
                <div className="mobile-card-field"><span className="mobile-card-label">Balance</span><span className="mobile-card-value">{formatAmount(loan.balance_amount)}</span></div>
                {loan.repledge_bank_name && <div className="mobile-card-field full"><span className="mobile-card-label">Bank / Pledge</span><span className="mobile-card-value">{loan.repledge_bank_name} / {loan.pledge_number}</span></div>}
              </div>
              {['Pledged', 'Overdue'].includes(status) && <button type="button" onClick={event => { event.stopPropagation(); setReleaseLoan(loan); }} style={{ marginTop: 10, padding: '7px 12px', border: '1px solid #d97706', borderRadius: 4, background: '#fff7ed', color: '#9a3412' }}>View / Release</button>}
            </div>
          );
        })}
      </div>

      {showPledgeModal && selectedLoans.length > 0 && (
        <GoldRepledgeModal loans={selectedLoans} onClose={() => setShowPledgeModal(false)} onSuccess={completeAction} />
      )}
      {releaseLoan && (
        <GoldRepledgeModal loan={releaseLoan} onClose={() => setReleaseLoan(null)} onSuccess={completeAction} />
      )}
    </div>
  );
}

export default GoldRepledges;
